/**
 * Lightweight MetaMask / window.ethereum wallet helpers.
 * Uses only the raw provider API — no wagmi, no heavy dependencies.
 */

import { POLYGON_AMOY_HEX, CHAIN_NAME, CHAIN_CURRENCY, CHAIN_EXPLORER } from "./wagmi";

declare global {
  interface Window {
    ethereum?: {
      isMetaMask?: boolean;
      request: (args: { method: string; params?: unknown[] }) => Promise<unknown>;
      on: (event: string, handler: (...args: unknown[]) => void) => void;
      removeListener: (event: string, handler: (...args: unknown[]) => void) => void;
    };
  }
}

export function hasMetaMask(): boolean {
  return typeof window !== "undefined" && !!window.ethereum;
}

export async function requestAccounts(): Promise<string[]> {
  if (!window.ethereum) throw new Error("MetaMask not found");
  const accounts = await window.ethereum.request({ method: "eth_requestAccounts" });
  return accounts as string[];
}

export async function getAccounts(): Promise<string[]> {
  if (!window.ethereum) return [];
  const accounts = await window.ethereum.request({ method: "eth_accounts" });
  return accounts as string[];
}

export async function getChainId(): Promise<number> {
  if (!window.ethereum) return 0;
  const hex = await window.ethereum.request({ method: "eth_chainId" });
  return parseInt(hex as string, 16);
}

export async function switchToAmoy(): Promise<void> {
  if (!window.ethereum) throw new Error("MetaMask not found");
  try {
    await window.ethereum.request({
      method: "wallet_switchEthereumChain",
      params: [{ chainId: POLYGON_AMOY_HEX }],
    });
  } catch (err: unknown) {
    const e = err as { code?: number };
    if (e?.code === 4902) {
      await window.ethereum.request({
        method: "wallet_addEthereumChain",
        params: [
          {
            chainId: POLYGON_AMOY_HEX,
            chainName: CHAIN_NAME,
            nativeCurrency: { name: CHAIN_CURRENCY, symbol: CHAIN_CURRENCY, decimals: 18 },
            rpcUrls: ["https://rpc-amoy.polygon.technology/"],
            blockExplorerUrls: [CHAIN_EXPLORER],
          },
        ],
      });
    } else {
      throw err;
    }
  }
}

/** Read ERC-20 balanceOf via raw eth_call (no library needed). */
export async function erc20BalanceOf(
  tokenAddress: string,
  walletAddress: string,
): Promise<bigint> {
  if (!window.ethereum || !tokenAddress || !walletAddress) return 0n;
  const paddedAddr = walletAddress.slice(2).toLowerCase().padStart(64, "0");
  const data = "0x70a08231" + paddedAddr;
  try {
    const result = await window.ethereum.request({
      method: "eth_call",
      params: [{ to: tokenAddress, data }, "latest"],
    });
    if (!result || result === "0x") return 0n;
    return BigInt(result as string);
  } catch {
    return 0n;
  }
}

export function formatTokenBalance(raw: bigint, decimals: number, maxFractionDigits = 2): string {
  const divisor = 10n ** BigInt(decimals);
  const whole = raw / divisor;
  const frac  = raw % divisor;
  const fracStr = frac.toString().padStart(decimals, "0").slice(0, maxFractionDigits);
  const num = parseFloat(`${whole}.${fracStr}`);
  return num.toLocaleString("en-US", { maximumFractionDigits: maxFractionDigits });
}
